import type { IAnalyticsEventFormField } from '@sravni/cosago-react-library/lib/types';
import type { FieldValues } from '@sravni/react-silent-authentication-form';
import { type FC, useCallback, useEffect } from 'react';

import { isSatisfyOption, useAppDispatch, useAppSelector } from 'shared/lib';
import {
  sendEventChangeFormFieldDefaultParam,
  sendEventFillFormField,
  sendEventShowStep,
  SendEventWrapper,
} from 'shared/lib/sendGAEvents';
import { useUpdateStateBySelector } from 'shared/lib/useUpdateStateBySelector';

import type { TContactsFormFields, TLandingFormFields } from 'entities/creditForm';
import {
  setLandingInfo,
  selectLandingInfoData,
  selectLandingInfoAuthData,
  LandingFormValidationSchema,
} from 'entities/creditForm';
import { isLoggedSelector } from 'entities/user';

import type { AuthFormProps } from 'features/UpdateContactsInfo';
import { AuthFormHoc } from 'features/UpdateContactsInfo';

import { FormFieldsControls } from '../../LandingInfo.config';
import { LandingInfoTexts, SubmitButtonAuthText } from '../../LandingInfo.texts';
import { submitLandingForm } from '../../lib/submitLandingForm';
import { FormBody } from '../FormBody/FormBody';
import { LandingInfoViewHoc } from '../LandingInfoViewHoc';

import styles from './Form.module.scss';

export const Form: FC<
  Pick<IFormPopup, 'onFormSubmit' | 'isLoading' | 'onAuthorizationSuccess' | 'analyticsOverrides'>
> = ({ onFormSubmit, isLoading, onAuthorizationSuccess: onAuthorizationSuccessCallback, analyticsOverrides }) => {
  const dispatch = useAppDispatch();
  const [formData] = useUpdateStateBySelector(selectLandingInfoData);
  const authDefaultValues = useAppSelector(selectLandingInfoAuthData);
  const isAuth = useAppSelector(isLoggedSelector);

  useEffect(() => {
    sendEventShowStep(1, 'Лендинг витрины', analyticsOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLandingInfoData = async (actualData: TLandingFormFields, type: string) => {
    if (type === 'fullFilled') {
      dispatch(submitLandingForm(actualData));
      onFormSubmit?.(actualData);
    }
  };

  const onAuthorizationSuccess = useCallback(
    (userInfo: TLandingFormFields & FieldValues) => {
      dispatch(
        setLandingInfo({
          data: {
            ...userInfo,
            fullName: userInfo.fullName,
            mobilePhone: String(userInfo.phone || ''),
            email: userInfo.email,
            userAgreement: userInfo.agreementPersonalData,
            advertisingAgreement: userInfo.agreementAdvertising,
          },
        }),
      );
      onAuthorizationSuccessCallback?.(userInfo);
      onFormSubmit?.(userInfo);
    },
    [dispatch, onFormSubmit, onAuthorizationSuccessCallback],
  );

  const sendAnalyticsEvent = useCallback(
    ({ fieldName }: IAnalyticsEventFormField) => {
      const isParameter = isSatisfyOption(['userAgreement', 'advertisingAgreement'], fieldName);
      if (isParameter) {
        sendEventChangeFormFieldDefaultParam(
          1,
          LandingInfoTexts[fieldName as keyof TContactsFormFields],
          analyticsOverrides,
        );
      } else {
        sendEventFillFormField(1, LandingInfoTexts[fieldName as keyof TContactsFormFields], true, analyticsOverrides);
      }
    },
    [analyticsOverrides],
  );

  if (!isAuth) {
    return (
      <SendEventWrapper sendEvent={sendAnalyticsEvent}>
        <AuthFormHoc
          authDefaultValues={{ ...authDefaultValues, agreementAdvertising: false, agreementPersonalData: true }}
          formLabels={LandingInfoTexts}
          formFieldsControls={FormFieldsControls}
          FormComponent={FormBody}
          isOTPButtonStickyEnabled={false}
          getOTPButtonProps={{
            variant: 'outlined',
            children: SubmitButtonAuthText,
            className: styles.otpButton,
            isInlineButton: true,
            loading: isLoading,
          }}
          validationSchema={LandingFormValidationSchema()}
          onAuthorizationSuccess={onAuthorizationSuccess as AuthFormProps['onAuthorizationSuccess']}
          isEnabledEmailField
        />
      </SendEventWrapper>
    );
  }

  return (
    <SendEventWrapper sendEvent={sendAnalyticsEvent}>
      <LandingInfoViewHoc
        formLabels={LandingInfoTexts}
        formFieldsControls={FormFieldsControls}
        defaultData={formData}
        validationSchema={LandingFormValidationSchema(true)}
        onDataChanged={handleLandingInfoData}
        isLoading={isLoading}
      />
    </SendEventWrapper>
  );
};
